import { createFileRoute, Link } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { getProcessingActivities } from "@/lib/popia";
import { enquiryMailto } from "@/lib/enquiry";
import { ENQUIRY_EMAIL } from "@/lib/brand";

export const Route = createFileRoute("/legal/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  const activities = getProcessingActivities();

  return (
    <MarketingLayout>
      <Helmet>
        <title>Privacy Notice | Larasoft</title>
        <meta
          name="description"
          content="Learn how Larasoft collects, uses, and protects your personal information in accordance with POPIA."
        />
        <link rel="canonical" href="https://larasoft.global/legal/privacy" />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-8 text-4xl font-bold">Privacy Notice</h1>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-700">
              Last updated: {new Date().toLocaleDateString("en-ZA")}
            </p>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">1. Introduction</h2>
              <p>
                Larasoft (Pty) Ltd respects your privacy and is committed to
                protecting your personal information in accordance with the
                Protection of Personal Information Act (POPIA) and other
                applicable data protection laws.
              </p>
              <p>
                This Privacy Notice explains how we collect, use, store, and
                protect your personal information when you use our cloud
                reporting and data integration services.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                2. Information Officer
              </h2>
              <p>
                Our Information Officer is responsible for POPIA compliance:
              </p>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="mb-2">
                  <strong>Contact:</strong> {ENQUIRY_EMAIL}
                </p>
                <p className="mb-2">
                  <strong>Phone:</strong> +27 82 457 8390
                </p>
                <p>
                  <strong>Address:</strong> Larasoft (Pty) Ltd, South Africa
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                3. What Information We Collect
              </h2>
              <p>
                We collect and process various types of personal information
                depending on how you interact with our services:
              </p>

              <div className="mt-6 space-y-6">
                {activities.slice(0, 5).map((activity, i) => (
                  <div
                    key={activity.processingActivityId || i}
                    className="rounded-lg border border-slate-200 p-6"
                  >
                    <h3 className="mb-2 text-xl font-semibold text-slate-900">
                      {activity.activityName}
                    </h3>
                    <p className="mb-4 text-slate-700">
                      {activity.description}
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <h4 className="mb-2 font-medium text-slate-900">
                          Data Categories
                        </h4>
                        <p className="text-sm text-slate-600">
                          {activity.dataCategoriesCollected}
                        </p>
                      </div>
                      <div>
                        <h4 className="mb-2 font-medium text-slate-900">
                          Purpose
                        </h4>
                        <p className="text-sm text-slate-600">
                          {activity.purposeOfProcessing}
                        </p>
                      </div>
                      <div>
                        <h4 className="mb-2 font-medium text-slate-900">
                          Legal Basis
                        </h4>
                        <p className="text-sm text-slate-600">
                          {activity.legalBasisPOPIA}
                        </p>
                      </div>
                      <div>
                        <h4 className="mb-2 font-medium text-slate-900">
                          Retention Period
                        </h4>
                        <p className="text-sm text-slate-600">
                          {activity.retentionPeriod}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {activities.length > 5 && (
                <p className="mt-6 text-sm text-slate-600">
                  For a complete list of processing activities, please see our{" "}
                  <Link
                    to="/legal/data-processing"
                    className="text-blue-600 underline"
                  >
                    Data Processing Register
                  </Link>
                  .
                </p>
              )}
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                4. How We Use Your Information
              </h2>
              <p>
                We use your personal information for the following purposes:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  To provide and maintain our cloud reporting services
                </li>
                <li>
                  To process and sync data from the applications you connect to
                  our platform. The systems we currently integrate with are
                  listed in full in our{" "}
                  <Link
                    to="/legal/data-processing"
                    className="text-blue-600 underline"
                  >
                    Data Processing Register
                  </Link>
                  .
                </li>
                <li>
                  To generate reports and analytics based on your business data
                </li>
                <li>
                  To provide customer support and respond to your inquiries
                </li>
                <li>
                  To send important service updates and notifications
                </li>
                <li>To comply with legal and regulatory requirements</li>
                <li>To improve and optimize our services</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                5. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational measures
                to protect your personal information:
              </p>
              <ul className="mt-4 space-y-2">
                <li>Encryption at rest and in transit (AES-256, TLS)</li>
                <li>
                  Secure cloud infrastructure hosted in South Africa (Azure
                  South Africa North)
                </li>
                <li>
                  Access controls and authentication (including MFA)
                </li>
                <li>Regular security audits and monitoring</li>
                <li>Secure backup and disaster recovery procedures</li>
                <li>Staff training on data protection and privacy</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                6. Third-Party Services
              </h2>
              <p>
                Third parties fall into two groups, and the distinction matters
                for who is accountable for your information.
              </p>

              <h3 className="mb-2 mt-6 text-lg font-semibold">
                Infrastructure we run our platform on
              </h3>
              <p>
                These providers process personal information on our instruction.
                We are accountable for them.
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  Microsoft Azure - cloud hosting and databases, South Africa
                  North region
                </li>
                <li>
                  AWS S3 - secure file storage for our file transfer service,
                  Cape Town (af-south-1)
                </li>
              </ul>

              <h3 className="mb-2 mt-6 text-lg font-semibold">
                Systems we connect to on our clients' instruction
              </h3>
              <p>
                When a client asks us to bring their data into their reporting
                warehouse, we read from the business systems they already use.
                We act as an operator on that client's documented instruction:
                the client remains the responsible party, and the client holds
                the relationship with each of these providers. The systems we
                currently read from are:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  Accounting and ERP: Xero, PowerOffice Go, GAAP and
                  GAAPUnity, and clients' own on-premise Informix systems
                </li>
                <li>
                  Inventory, retail and eCommerce: CIN7, Vend and Lightspeed,
                  WooCommerce, and Wherehouse
                </li>
                <li>
                  CRM and sales: Salesforce, HubSpot, Zoho CRM, and Bitrix24
                </li>
                <li>
                  Marketing and advertising: Mailchimp, Google Ads, and Meta Ads
                </li>
                <li>
                  Project, work and time management: Asana, monday.com, Zoho
                  Projects, and Harvest
                </li>
                <li>
                  Payroll and HR: PaySpace, Deel, and SimplePay
                </li>
                <li>
                  Healthcare: MedicalDirector Helix. This system carries health
                  information, which is special personal information under
                  POPIA. We process it only on the documented instruction of the
                  healthcare provider that holds the patient relationship, and
                  we assert no independent ground for processing it.
                </li>
                <li>
                  Clients' own databases, where no supported integration
                  exists: MySQL databases, read only within the scope the
                  client nominates
                </li>
              </ul>

              <p className="mt-4">
                We also support, but do not currently read from on any client's
                behalf, Sage Business Cloud Accounting, Uniconta and generic
                OData endpoints. They are listed here because the capability
                exists and a client could ask us to enable it, not because data
                is flowing today.
              </p>

              <p className="mt-4">
                The{" "}
                <Link
                  to="/legal/data-processing"
                  className="text-blue-600 underline"
                >
                  Data Processing Register
                </Link>{" "}
                is the authoritative record of this list. It sets out, for every
                activity, what data is processed, on what legal basis, where it
                is stored, how long it is kept, and whether it crosses a border.
                This section summarises that register; where the two differ, the
                register is correct.
              </p>
              <p className="mt-4">
                We are working through data processing and operator agreements
                across this set. Where an agreement is not yet in place, that
                gap is recorded in our internal risk register rather than
                treated as closed.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                7. Your Rights Under POPIA
              </h2>
              <p>
                You have the following rights regarding your personal
                information:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  <strong>Right to Access:</strong> Request a copy of your
                  personal information
                </li>
                <li>
                  <strong>Right to Correction:</strong> Request correction of
                  inaccurate information
                </li>
                <li>
                  <strong>Right to Deletion:</strong> Request deletion of your
                  personal information (subject to legal retention requirements)
                </li>
                <li>
                  <strong>Right to Object:</strong> Object to processing of your
                  personal information
                </li>
                <li>
                  <strong>Right to Data Portability:</strong> Request your data
                  in a portable format
                </li>
                <li>
                  <strong>Right to Lodge a Complaint:</strong> Complain to the
                  Information Regulator
                </li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact our Information
                Officer at{" "}
                <a
                  href={enquiryMailto("/legal/privacy")}
                  className="text-blue-600 underline"
                >
                  {ENQUIRY_EMAIL}
                </a>
                .
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                8. International Data Transfers
              </h2>
              <p>
                We store and process data in South Africa. Our databases, file
                storage and application infrastructure are hosted in the Azure
                South Africa North region and in AWS Cape Town, and the
                reporting warehouse we build for a client is held here.
              </p>
              <p className="mt-4">
                Data crosses a border in two different ways, and they are not
                the same thing in law or in practice. We separate them here
                because a single combined list obscures which transfers are
                ours to justify.
              </p>

              <h3 className="mb-2 mt-6 text-lg font-semibold">
                Reading in: data we bring into South Africa
              </h3>
              <p>
                Most of what we do is read a business system a client already
                uses and copy the data into their warehouse here. If that system
                is hosted abroad, the data moves into South Africa. The client
                chose to put their data in that system before we were involved,
                and that choice is theirs to account for; our step moves a copy
                closer to home rather than further away. The origins this
                currently involves are:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  United States - Mailchimp, Google Ads, Meta Ads, Asana,
                  Harvest, and the US regions of Salesforce, HubSpot,
                  monday.com, Bitrix24 and Zoho
                </li>
                <li>
                  European Union - the EU regions of Salesforce, HubSpot,
                  monday.com, Bitrix24, Zoho, Google Ads and Meta Ads
                </li>
                <li>
                  Australia - MedicalDirector Helix, and the Australian region
                  of Xero. The Helix flow carries health information and is
                  treated as our highest-sensitivity read.
                </li>
                <li>Norway - PowerOffice Go</li>
                <li>India - the Indian region of Zoho CRM and Zoho Projects</li>
                <li>
                  Canada - Lightspeed, for clients using Vend or Lightspeed
                  point of sale
                </li>
                <li>
                  Varies by client - CIN7, WooCommerce stores, and clients' own
                  databases and endpoints, whose location the client chooses
                </li>
              </ul>

              <h3 className="mb-2 mt-6 text-lg font-semibold">
                Writing out: data we send out of South Africa
              </h3>
              <p>
                Some clients ask us to post data from their warehouse back into
                an operational system, so the two agree. Where that system is
                hosted abroad, we are the party sending personal information out
                of South Africa, and the responsibility for justifying the
                transfer is ours rather than the client's. This is the smaller
                of the two flows and the one we hold ourselves to most strictly.
                It currently goes to:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  Xero - accounting write-back, to whichever region hosts the
                  client's own Xero account. Xero operates data centres in
                  Australia, the United States and the EU; which one applies to
                  a given client is set by that client's Xero account and is
                  something we are establishing rather than something we
                  currently record
                </li>
                <li>Asana - project write-back, United States</li>
                <li>
                  Client-nominated file transfer endpoints, including a European
                  endpoint used for logistics document exchange
                </li>
              </ul>
              <p className="mt-4">
                Which of these apply to any one client depends entirely on the
                systems that client asks us to connect. A client whose systems
                are all South African has no cross-border flow at all, in either
                direction. The{" "}
                <Link
                  to="/legal/data-processing"
                  className="text-blue-600 underline"
                >
                  Data Processing Register
                </Link>{" "}
                records the position per activity, and marks the direction.
              </p>
              <p className="mt-4">
                For the transfers we send out, these are the safeguards that
                apply. We have marked which are in place today and which we are
                still putting in place, because listing an intended safeguard
                as a current one would misdescribe our position:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  A lawful ground under section 72 of POPIA for each
                  destination. We are determining which ground applies to each
                  one rather than asserting a single answer here, because the
                  people the data describes are our clients' customers rather
                  than our clients themselves, and that distinction changes the
                  analysis.
                </li>
                <li>
                  Written operator agreements recording the systems, the fields
                  and the destinations in scope. Not yet in place. We are
                  drafting a standard agreement and executing it across our
                  client base, and until that is done this safeguard is an
                  undertaking rather than a control.
                </li>
                <li>
                  Sending only the records the client nominates, rather than
                  mirroring a whole dataset outward. In place.
                </li>
                <li>
                  Encryption of the data in transit to every destination. In
                  place.
                </li>
              </ul>
              <p className="mt-4">
                We are completing this work rather than reporting it as done.
                Where a ground or an agreement is not yet recorded for a
                destination, it is carried in our internal risk register as an
                open item, not treated as closed.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                9. Data Retention
              </h2>
              <p>
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="mt-4 space-y-2">
                <li>Provide our services to you</li>
                <li>
                  Comply with legal and regulatory requirements (typically 7
                  years for financial data)
                </li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="mt-4">
                When we no longer need your information, we securely delete or
                anonymize it.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                10. Changes to This Privacy Notice
              </h2>
              <p>
                We may update this Privacy Notice from time to time to reflect
                changes in our practices or legal requirements. We will notify
                you of significant changes via email or through our website.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">11. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Notice or our
                privacy practices, please contact us:
              </p>
              <div className="mt-4 rounded-lg bg-slate-50 p-4">
                <p className="mb-2">
                  <strong>Email:</strong>{" "}
                  <a
                    href={enquiryMailto("/legal/privacy")}
                    className="text-blue-600 underline"
                  >
                    {ENQUIRY_EMAIL}
                  </a>
                </p>
                <p className="mb-2">
                  <strong>Phone:</strong> +27 82 457 8390
                </p>
                <p>
                  <strong>Information Regulator:</strong>{" "}
                  <a
                    href="https://www.justice.gov.za/inforeg/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    www.justice.gov.za/inforeg
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
